import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { brandInfo, testimonials } from '@/mocks/douhuaData';


function AboutSEO() {
  useDocumentHead({
    title: '关于我们 - 豆花视频 | 品牌介绍与联系方式',
    description: '豆花视频品牌介绍：免费在线观看高清影视平台，汇集西瓜视频、芒果视频、丝瓜视频等资源。了解豆花视频的发展历程、品牌口碑与联系方式。',
    keywords: '豆花视频,关于我们,品牌介绍,联系方式,免费在线观看,影视平台',
    canonicalUrl: 'https://douhua.example.com/about',
  });

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于豆花视频',
    description: '豆花视频是一家专注于免费在线影视内容导航的互联网平台，致力于为用户提供高清流畅的观影体验。',
    url: 'https://douhua.example.com/about',
    about: {
      '@type': 'Organization',
      name: '豆花视频',
      description: '免费在线观看高清影视平台',
    },
  };

  return (
    <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
  );
}

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const serverMsg = (parsed as Record<string, unknown>)?.meta as Record<string, string> | undefined;
        setFormError(serverMsg?.message || serverMsg?.detail || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <ScrollToTop />
      <AboutSEO />
      <Navbar />

      <main className="pt-20 md:pt-24 pb-12">
        <div className="w-full max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

          {/* Hero */}
          <section className="my-8 text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600 mb-5">
              <i className="ri-movie-2-line text-3xl"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">关于豆花视频</h1>
            <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {brandInfo.intro}
            </p>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
            {brandInfo.stats.map((stat, idx) => (
              <div key={idx} className="text-center p-6 bg-background-100 rounded-xl border border-background-200">
                <p className="text-2xl md:text-3xl font-bold text-primary-500">{stat.value}</p>
                <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </section>

          {/* Mission & Vision */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            <div className="p-6 bg-background-100 rounded-xl border border-background-200">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                <i className="ri-focus-3-line text-xl"></i>
              </div>
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-2">我们的使命</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{brandInfo.mission}</p>
            </div>
            <div className="p-6 bg-background-100 rounded-xl border border-background-200">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                <i className="ri-eye-line text-xl"></i>
              </div>
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-2">我们的愿景</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{brandInfo.vision}</p>
            </div>
          </section>

          {/* Values */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">核心价值观</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {brandInfo.values.map((val, idx) => (
                <div key={idx} className="text-center p-5 bg-background-100 rounded-xl border border-background-200">
                  <p className="text-base font-medium text-foreground-800">{val}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Milestones */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2"></div>
              <div className="space-y-6">
                {brandInfo.milestones.map((m, idx) => (
                  <div key={idx} className={`relative flex items-center ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'} pl-12 md:pl-0`}>
                      <div className="bg-background-100 p-4 rounded-xl border border-background-200 inline-block">
                        <p className="text-sm font-medium text-accent-600">{m.year}</p>
                        <p className="text-sm text-foreground-700 mt-0.5">{m.event}</p>
                      </div>
                    </div>
                    <div className="absolute md:static left-3 md:left-1/2 md:-translate-x-1/2 w-3 h-3 bg-primary-500 rounded-full z-10 border-2 border-background-50"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t, idx) => (
                <div key={idx} className="p-5 bg-background-100 rounded-xl border border-background-200">
                  <div className="flex items-center gap-3 mb-4">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <div className="flex gap-0.5 mt-3 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-background-100 rounded-xl border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电子邮箱</p>
                      <p className="text-sm text-foreground-700">{brandInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-background-100 rounded-xl border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">微信公众号</p>
                      <p className="text-sm text-foreground-700">{brandInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 bg-background-100 rounded-xl border border-background-200">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-weibo-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">微博</p>
                      <p className="text-sm text-foreground-700">{brandInfo.contact.weibo}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <ContactForm formStatus={formStatus} formError={formError} onSubmit={handleSubmit} />
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}

function ContactForm({ formStatus, formError, onSubmit }: {
  formStatus: string;
  formError: string;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <form
      data-readdy-form
      id="contact-form"
      action="https://readdy.ai/api/form/d9n1hekit35ii3jdlncg"
      method="POST"
      onSubmit={onSubmit}
      className="bg-background-100 p-5 rounded-xl border border-background-200 space-y-4"
    >
      <h3 className="text-base font-heading font-bold text-foreground-900">发送消息</h3>

      <div>
        <label htmlFor="contact-name" className="block text-xs text-foreground-500 mb-1">姓名</label>
        <input
          type="text"
          id="contact-name"
          name="name"
          required
          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
          placeholder="请输入您的姓名"
        />
      </div>

      <div>
        <label htmlFor="contact-email" className="block text-xs text-foreground-500 mb-1">邮箱</label>
        <input
          type="email"
          id="contact-email"
          name="email"
          required
          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
          placeholder="请输入您的邮箱"
        />
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-xs text-foreground-500 mb-1">留言内容（限500字）</label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
          placeholder="请描述您的问题或建议..."
        ></textarea>
      </div>

      <input
        type="text"
        name="phone_alt"
        className="anti-spam-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={formStatus === 'loading'}
        className="w-full py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-lg font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
      >
        {formStatus === 'loading' ? (
          <><i className="ri-loader-4-line animate-spin mr-2"></i>提交中...</>
        ) : '发送消息'}
      </button>

      {formStatus === 'success' && (
        <p className="text-sm text-green-600 text-center">消息已发送成功！我们会尽快回复您。</p>
      )}
      {formStatus === 'error' && (
        <p className="text-sm text-red-500 text-center">{formError}</p>
      )}
    </form>
  );
}